// Files placed INSIDE a note (migration 234) — the client half.
//
// A note stores a file as a BlockNote image/video/audio/file block whose URL is
// a stable reference, `harvus-file://notebook/<FileID>`; each client resolves it
// to a short-lived link when it renders (GET /api/notebook-files/{id}). Mirrors
// api/src/notebookFiles.js — keep the prefix and blockTypeFor in lockstep.

export const NOTEBOOK_FILE_PREFIX = "harvus-file://notebook/";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function notebookFileIdFromUrl(url: unknown): string | null {
  const s = String(url || "");
  if (!s.startsWith(NOTEBOOK_FILE_PREFIX)) return null;
  const id = s.slice(NOTEBOOK_FILE_PREFIX.length).split(/[?#/]/)[0];
  return UUID_RE.test(id) ? id.toLowerCase() : null;
}

export type NotebookBlockType = "image" | "video" | "audio" | "file";

export function notebookBlockTypeFor(contentType: unknown, fileName: unknown): NotebookBlockType {
  const ct = String(contentType || "").toLowerCase();
  const ext = String(fileName || "").toLowerCase().split(".").pop() || "";
  if (ct.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "heic", "heif", "bmp", "svg"].includes(ext)) return "image";
  if (ct.startsWith("video/") || ["mp4", "mov", "m4v", "webm"].includes(ext)) return "video";
  if (ct.startsWith("audio/") || ["mp3", "m4a", "wav", "aac", "ogg"].includes(ext)) return "audio";
  return "file";
}

interface ResolveDeps {
  get: (path: string) => Promise<{ url?: string | null }>;
  now?: () => number;
}

/**
 * A resolver for BlockNote's `resolveFileUrl`: a note-file reference becomes a
 * read link (cached for 40 minutes, under the server's 60-minute SAS); anything
 * else passes through untouched. A failed resolve returns the reference itself,
 * which renders as a broken image rather than throwing inside the editor.
 */
export function createNotebookFileResolver({ get, now = () => Date.now() }: ResolveDeps) {
  const cache = new Map<string, { url: string; at: number }>();
  const inflight = new Map<string, Promise<string>>();
  const TTL = 40 * 60 * 1000;
  return async function resolve(url: string): Promise<string> {
    const id = notebookFileIdFromUrl(url);
    if (!id) return url;
    const hit = cache.get(id);
    if (hit && now() - hit.at < TTL) return hit.url;
    const running = inflight.get(id);
    if (running) return running;
    const p = get(`notebook-files/${id}`)
      .then((r) => {
        const u = r?.url ? String(r.url) : url;
        if (r?.url) cache.set(id, { url: u, at: now() });
        return u;
      })
      .catch(() => url)
      .finally(() => { inflight.delete(id); });
    inflight.set(id, p);
    return p;
  };
}
